/* DrawingsHub — single grouped surface.
 *
 * Per user direction ("ultra think"): no parallel reference tables.
 * One narrative — group by drawing class. For each of the six typed
 * classes show, in one card:
 *
 *   • What it is              — class label + ontology extends
 *   • Where it comes from     — public datasets / file formats
 *   • Coverage on this project — count, with actual instances below
 *   • Rules ready             — how many library rules can watch this kind
 *   • Action when empty       — concrete next step (drop an IFC4 file, etc.)
 *
 * Below: registered dataset catalogue.
 *
 * Everything comes from the live project graph + codegen.json. No
 * hardcoding.
 */

import { useMemo } from "react";
import {
  Layers,
  PanelTop,
  Scissors,
  LampCeiling,
  PlugZap,
  Boxes,
  ExternalLink,
  Inbox,
  Ruler,
  Eye,
  EyeOff,
  UploadCloud,
} from "lucide-react";
import codegen from "../../../../docs/ontology/codegen.json";
import { DRAWING_DATASETS } from "../../lib/datasets";
import { DRAWING_STANDARDS } from "../../lib/drawingStandards";
import { DESIGNER_RULES, type DesignerRule } from "../../data/designerRules";
import { useActiveProjectGraph } from "../../lib/useActiveProjectGraph";
import { renderFloorPlanSvg } from "../../lib/floorplanSvg";
import { cls } from "../../lib/cls";
import type { GraphNode, ProjectGraph } from "../../types/ontology";

const DRAWING_KINDS = [
  "FloorPlan",
  "Elevation",
  "Section",
  "ReflectedCeilingPlan",
  "MEPPlan",
  "Scene3D",
] as const;
type DrawingKind = (typeof DRAWING_KINDS)[number];

const GLYPH: Record<DrawingKind, JSX.Element> = {
  FloorPlan:            <Layers      className="w-4 h-4" />,
  Elevation:            <PanelTop    className="w-4 h-4" />,
  Section:              <Scissors    className="w-4 h-4" />,
  ReflectedCeilingPlan: <LampCeiling className="w-4 h-4" />,
  MEPPlan:              <PlugZap     className="w-4 h-4" />,
  Scene3D:              <Boxes       className="w-4 h-4" />,
};

const KIND_LABEL: Record<DrawingKind, string> = {
  FloorPlan:            "Floor plan",
  Elevation:            "Elevation",
  Section:              "Section",
  ReflectedCeilingPlan: "Reflected ceiling plan",
  MEPPlan:              "MEP plan",
  Scene3D:              "3D scene",
};

const CLASS_DATASETS: Record<DrawingKind, string[]> = {
  FloorPlan:            ["CubiCasa5K", "RPLAN", "FloorPlanCAD"],
  Elevation:            ["IFC4"],
  Section:              ["IFC4"],
  ReflectedCeilingPlan: [],
  MEPPlan:              [],
  Scene3D:              ["IFC4", "GLTF2", "Matterport3D", "ThreeDFront", "USD"],
};

const POPULATE_HINT: Record<DrawingKind, string> = {
  FloorPlan:            "Drop a CubiCasa5K SVG / DXF / RPLAN export — the ingest pipeline maps it to typed FloorPlan nodes.",
  Elevation:            "Drop an IFC4 BIM export (Revit / ArchiCAD / Vectorworks). Elevations derive from the wall planes.",
  Section:              "Drop an IFC4 BIM export. Sections derive from cut-line metadata on the model.",
  ReflectedCeilingPlan: "Generated from the 3D scene topology (Three.js shader) — no public dataset yet. Manual SVG upload also accepted.",
  MEPPlan:              "Upload a vector MEP overlay (DWG → SVG). NBC 2016 Part 8 + Part 9 fixture rules light up once instances land.",
  Scene3D:              "Drop an IFC4 BIM file, a glTF 2.0 export, or a Matterport scan. The matterportManager already wires the third path.",
};

interface NodeMetaRaw { label?: string; extends?: string[] }

function classDescription(kind: DrawingKind): string {
  const m = (codegen.nodes as Record<string, NodeMetaRaw | undefined>)[kind];
  if (!m) return "";
  const extendsList = m.extends ?? [];
  if (extendsList.length === 0) return "";
  return extendsList.join(" · ");
}

function rulesReadingClass(kind: DrawingKind): DesignerRule[] {
  const out: DesignerRule[] = [];
  for (const r of DESIGNER_RULES) {
    const s = JSON.stringify(r.predicate);
    if (s.includes(`"${kind}.`) || s.includes(`"nodeKind":"${kind}"`)) out.push(r);
  }
  return out;
}

function attr<T>(n: GraphNode | undefined, k: string): T | undefined {
  if (!n) return undefined;
  const v = n.attrs[k];
  return v === undefined ? undefined : (v as T);
}

interface DrawingInstance {
  id: string;
  label: string;
  kind: DrawingKind;
  parentRoomLabel?: string;
  planSource: string;
  svg: string;
  metaPills: Array<{ k: string; v: string }>;
}

function instancesByKind(graph: ProjectGraph): Record<DrawingKind, DrawingInstance[]> {
  const out: Record<DrawingKind, DrawingInstance[]> = {
    FloorPlan: [], Elevation: [], Section: [], ReflectedCeilingPlan: [], MEPPlan: [], Scene3D: [],
  };

  // FloorPlans — derived from Room HAS_FLOORPLAN edges, with the
  // existing CubiCasa5K-shape vector renderer.
  for (const room of graph.nodes.filter((n) => n.kind === "Room")) {
    const planEdge = graph.edges.find((e) => e.kind === "HAS_FLOORPLAN" && e.source === room.id);
    if (!planEdge) continue;
    const plan = graph.nodes.find((n) => n.id === planEdge.target && n.kind === "FloorPlan");
    if (!plan) continue;
    const svg = renderFloorPlanSvg({ graph, roomId: room.id, width: 320, height: 200 });
    const widthFt = attr<number>(plan, "widthFt");
    const depthFt = attr<number>(plan, "depthFt");
    const roomArea = attr<number>(room, "area");
    const metaPills: Array<{ k: string; v: string }> = [];
    if (widthFt !== undefined && depthFt !== undefined) metaPills.push({ k: "size", v: `${widthFt} × ${depthFt} ft` });
    if (roomArea !== undefined) metaPills.push({ k: "area", v: `${roomArea} sq ft` });
    out.FloorPlan.push({
      id: plan.id,
      label: plan.label,
      kind: "FloorPlan",
      parentRoomLabel: room.label,
      planSource: String(attr<string>(plan, "planSource") ?? "CubiCasa5K"),
      svg,
      metaPills,
    });
  }

  // Other drawing kinds — typed instances directly on the project graph
  // (Scene3D, Elevation, …). No SVG renderer yet for these.
  for (const n of graph.nodes) {
    const k = n.kind as DrawingKind;
    if (!DRAWING_KINDS.includes(k)) continue;
    if (k === "FloorPlan") continue; // handled above
    out[k].push({
      id: n.id,
      label: n.label,
      kind: k,
      planSource: String(attr<string>(n, "planSource") ?? attr<string>(n, "source") ?? KIND_LABEL[k]),
      svg: "",
      metaPills: [],
    });
  }

  return out;
}

export function DrawingsHub(): JSX.Element {
  const { graph } = useActiveProjectGraph();
  const grouped = useMemo(() => instancesByKind(graph), [graph]);

  const totalInstances = useMemo(
    () => DRAWING_KINDS.reduce((acc, k) => acc + grouped[k].length, 0),
    [grouped],
  );

  return (
    <div className="space-y-5">
      {/* Single headline */}
      <div className="panel p-4 sm:p-5 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Ruler className="w-3.5 h-3.5 text-foyr" />
          <div>
            <div className="eyebrow text-foyr">Architectural drawings on {graph.projectName}</div>
            <p className="text-[12px] text-ink-700 mt-1 max-w-2xl">
              A drawing belongs to the project; a rule may or may not read fields on it. Grouped below by typed
              class — each card lists the public standard that populates it on ingest and the rules ready to watch it.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 tabular text-[11px]">
          <span className="pill bg-fern/10 border border-fern/30 text-fern">{totalInstances} drawing{totalInstances === 1 ? "" : "s"}</span>
          <span className="pill bg-cream-dark border border-border text-ink-700">6 typed classes</span>
        </div>
      </div>

      {/* Grouped-by-class. Each block: class header → real instances OR empty-state with how-to-populate. */}
      {DRAWING_KINDS.map((k) => {
        const meta = (codegen.nodes as Record<string, NodeMetaRaw | undefined>)[k];
        const ext = classDescription(k);
        const items = grouped[k];
        const rulesReady = rulesReadingClass(k);
        const datasets = CLASS_DATASETS[k]
          .map((id) => DRAWING_STANDARDS.find((s) => s.id === id))
          .filter((s): s is NonNullable<typeof s> => s !== undefined);

        return (
          <div key={k} className="panel p-4 sm:p-5 space-y-3">
            {/* Class header */}
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-3 min-w-0">
                <span className="w-9 h-9 rounded-xl bg-sea/10 text-sea grid place-items-center shrink-0">{GLYPH[k]}</span>
                <div className="min-w-0">
                  <div className="text-[15px] font-semibold text-ink-950 leading-tight">{meta?.label ?? KIND_LABEL[k]}</div>
                  <div className="text-[11px] text-ink-500 font-mono leading-tight">extends {ext}</div>
                </div>
              </div>
              <div className="flex items-center gap-2 tabular text-[11px]">
                <span className={cls(
                  "pill border",
                  items.length > 0 ? "bg-fern/10 border-fern/30 text-fern" : "bg-cream-dark border-border text-ink-500",
                )}>
                  {items.length} on project
                </span>
                <span className={cls(
                  "pill border inline-flex items-center gap-1",
                  rulesReady.length > 0 ? "bg-foyr/10 border-foyr/30 text-foyr-deep" : "bg-cream-dark border-border text-ink-500",
                )}>
                  {rulesReady.length > 0 ? <Eye className="w-2.5 h-2.5" /> : <EyeOff className="w-2.5 h-2.5" />}
                  {rulesReady.length} rule{rulesReady.length === 1 ? "" : "s"} ready
                </span>
              </div>
            </div>

            {/* Source standards */}
            <div className="flex items-center gap-2 flex-wrap text-[11px]">
              <span className="eyebrow">Populated from</span>
              {datasets.length === 0 ? (
                <span className="text-ink-500 italic">3D-derived · no public dataset</span>
              ) : (
                datasets.map((d) => (
                  <span key={d.id} className="pill bg-cream-dark border border-border text-ink-700 text-[10px]" title={d.citation}>{d.label}</span>
                ))
              )}
            </div>

            {/* Instances OR empty-state */}
            {items.length === 0 ? (
              <div className="rounded-xl border border-dashed border-border bg-cream-dark p-4 flex items-start gap-3">
                <span className="w-8 h-8 rounded-lg bg-ink-950 text-cream grid place-items-center shrink-0">
                  <UploadCloud className="w-3.5 h-3.5" />
                </span>
                <div>
                  <div className="text-[13px] text-ink-950 font-medium">Nothing on this project yet.</div>
                  <div className="text-[12px] text-ink-700 leading-snug mt-0.5">{POPULATE_HINT[k]}</div>
                  {rulesReady.length > 0 ? (
                    <div className="text-[11px] text-ink-500 mt-1">
                      {rulesReady.length} library rule{rulesReady.length === 1 ? "" : "s"} are typed to read this class — they activate the moment instances land.
                    </div>
                  ) : null}
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                {items.map((d) => (
                  <div key={d.id} className="panel-subtle p-3 flex flex-col gap-2">
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <div className="text-[13px] font-semibold text-ink-950 leading-tight truncate">
                          {d.parentRoomLabel ?? d.label}
                        </div>
                        <div className="text-[10px] text-ink-500 leading-tight font-mono">{d.id}</div>
                      </div>
                    </div>
                    {d.svg.length > 0 ? (
                      <div
                        className="rounded-lg border border-border bg-cream p-2 overflow-hidden"
                        dangerouslySetInnerHTML={{ __html: d.svg }}
                      />
                    ) : null}
                    {d.metaPills.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {d.metaPills.map((p) => (
                          <span key={p.k} className="pill bg-cream-dark border border-border text-ink-700 text-[10px] tabular">{p.v}</span>
                        ))}
                      </div>
                    ) : null}
                    <div className="text-[10px] text-ink-500 font-mono truncate" title={d.planSource}>↳ {d.planSource}</div>
                  </div>
                ))}
              </div>
            )}

            {/* Rules ready — collapsible-feel mini-list, only when rules exist. */}
            {rulesReady.length > 0 ? (
              <div className="border-t border-border pt-3">
                <div className="eyebrow mb-2">Rules ready to watch · {rulesReady.length}</div>
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-3 gap-y-1">
                  {rulesReady.slice(0, 6).map((r) => (
                    <li key={r.id} className="text-[11px] text-ink-700 leading-snug flex items-start gap-1.5">
                      <ExternalLink className="w-2.5 h-2.5 mt-0.5 shrink-0 text-ink-500" />
                      <a className="hover:text-ink-950" href={`/rules?tab=builder&from=${encodeURIComponent(r.id)}`}>
                        {r.title} <span className="ml-1 text-[10px] font-mono text-ink-500">{r.id}</span>
                      </a>
                    </li>
                  ))}
                  {rulesReady.length > 6 ? (
                    <li className="text-[10px] text-ink-500 italic">+ {rulesReady.length - 6} more</li>
                  ) : null}
                </ul>
              </div>
            ) : (
              <div className="text-[11px] text-ink-500 italic flex items-center gap-1">
                <Inbox className="w-3 h-3" /> No library rule reads this class today — open the builder to compose one.
              </div>
            )}
          </div>
        );
      })}

      {/* Dataset catalogue footer */}
      <div className="panel-subtle p-4">
        <div className="eyebrow mb-2">All registered drawing datasets · {DRAWING_DATASETS.length}</div>
        <div className="flex flex-wrap gap-1.5">
          {DRAWING_DATASETS.map((d) => (
            <span key={d.id} className="pill bg-cream-dark border border-border text-ink-700 text-[11px]" title={d.citation}>
              {d.label}
            </span>
          ))}
        </div>
        <p className="text-[10px] text-ink-500 mt-2 leading-snug">
          Source of truth: <span className="font-mono">docs/ontology/codegen.json#drawingStandards</span> +
          the <span className="font-mono">:DrawingStandard</span> instances in the TTL.
        </p>
      </div>
    </div>
  );
}
